// ═══════════════════════════════════════════════
//   KIMBAP STACK — stacker minigame
// ═══════════════════════════════════════════════
//
// Objective:   Stack kimbap slices on the plate as high as possible
//              without missing the column.
// Type:        timing
// Description: Click / SPACE / tap to drop the moving slice. Overhang gets
//              trimmed each drop; perfect drops keep full width and chain.
//              Speed ramps every piece (capped at GAME_STACKER.speedMax);
//              missing the stack ends the run. Reward scales with pieces.
// Tunables:    GAME_STACKER in data.js.

const {S, canvas, ctx, claimGameReward, addCleanup, showGameOver} = window;

// Layout constants — visual sizing, not difficulty
const STK_PIECE_H   = 16;  // px height of each kimbap slice
const STK_PIECE_GAP = 3;   // px gap between slices
const STK_PLATE_Y   = 280; // y of plate top edge
const STK_PLATE_H   = 26;  // px height of plate

// Filling colors, cycling per piece (carrot, cucumber, salmon, radish, tuna, purple yam)
const STK_FILLINGS = ['#f97316', '#22c55e', '#ec4899', '#f59e0b', '#3b82f6', '#a78bfa'];

// ── Shared window state so stopGame() in launcher.js can reset them ──
window.stkStack    = [];
window.stkCurrent  = null;
window.stkScrollY  = 0;

// ── Helpers ──────────────────────────────────────────────────────────

function stkPieceY(n) {
    // Visual y of piece at index n, accounting for camera scroll
    return STK_PLATE_Y - (n + 1) * (STK_PIECE_H + STK_PIECE_GAP) + window.stkScrollY;
}

function stkSpawnNext() {
    const CFG   = window.GAME_STACKER;
    const stack = window.stkStack;
    const baseW = stack.length > 0 ? stack[stack.length - 1].w : CFG.initW;
    const speed = Math.min(CFG.speedMax, CFG.speedBase + stack.length * CFG.speedRamp);
    const dir   = Math.random() < 0.5 ? 1 : -1;
    window.stkCurrent = {
        x:       dir > 0 ? 0 : canvas.width - baseW,
        w:       baseW,
        dir,
        speed,
        filling: STK_FILLINGS[stack.length % STK_FILLINGS.length]
    };
}

// ── Game lifecycle ────────────────────────────────────────────────────

function startStacker() {
    window.gameRunning = true;
    window.gameScore   = 0;
    window.stkStack    = [];
    window.stkCurrent  = null;
    window.stkScrollY  = 0;

    document.getElementById('mg-score').textContent = '0';
    document.getElementById('mg-timer').textContent = '';

    stkSpawnNext();
    window.gameInterval = setInterval(stkLoop, 16);
    addCleanup(() => clearInterval(window.gameInterval));

    canvas.addEventListener('click', stkDrop);
    canvas.addEventListener('touchstart', stkDropTouch, {passive: false});
    document.addEventListener('keydown', stkKey);
    addCleanup(() => canvas.removeEventListener('click', stkDrop));
    addCleanup(() => canvas.removeEventListener('touchstart', stkDropTouch));
    addCleanup(() => document.removeEventListener('keydown', stkKey));
    addCleanup(() => {
        window.stkStack   = [];
        window.stkCurrent = null;
        window.stkScrollY = 0;
    });
}

function stkDrop() {
    if (!window.gameRunning || !window.stkCurrent) return;
    const CFG   = window.GAME_STACKER;
    const stack = window.stkStack;
    const cur   = window.stkCurrent;

    // Reference is the last stacked piece, or the centered initial position
    const ref = stack.length > 0
        ? stack[stack.length - 1]
        : {x: canvas.width / 2 - CFG.initW / 2, w: CFG.initW};

    const ox1 = Math.max(cur.x, ref.x);
    const ox2 = Math.min(cur.x + cur.w, ref.x + ref.w);
    const ow  = ox2 - ox1;

    if (ow <= 2) {
        // Missed — game over
        window.stkCurrent = null;
        SFX.play('gameover');
        endStacker(false);
        return;
    }

    const perfect = Math.abs(cur.x - ref.x) <= CFG.perfectTol;

    stack.push({
        x:       perfect ? ref.x : ox1,
        w:       perfect ? ref.w : ow,
        filling: cur.filling,
        flash:   perfect ? 24 : 0
    });

    window.gameScore = stack.length;
    document.getElementById('mg-score').textContent = stack.length;

    if (perfect) {
        SFX.play('pts');
        toast('PERFECT! 🎯');
    } else {
        SFX.play('hit');
    }

    // Scroll camera so the next piece always appears near the top of the visible area
    const nextVisY = stkPieceY(stack.length);
    if (nextVisY < 55) {
        window.stkScrollY += 55 - nextVisY;
    }

    if (stack.length >= CFG.winPieces) {
        endStacker(true);
        return;
    }

    stkSpawnNext();
}

function stkDropTouch(e) {
    if (!window.gameRunning) return;
    e.preventDefault();
    stkDrop();
}

function stkKey(e) {
    if (!window.gameRunning || S.currentGame !== 'stacker') return;
    if (e.code === 'Space' || e.code === 'Enter') {
        e.preventDefault();
        stkDrop();
    }
}

function endStacker(win) {
    window.gameRunning = false;
    window.stkCurrent = null;

    const CFG   = window.GAME_STACKER;
    const score = window.gameScore;

    SFX.play(win ? 'win' : 'gameover');
    if (score >= CFG.nickThreshold) window.unlockNick('stacker');

    const p = Math.min(CFG.rewardMax, score * CFG.rewardPer);
    showGameOver(claimGameReward('stacker', score, p));
}

// ── Render loop ───────────────────────────────────────────────────────

function stkLoop() {
    if (!window.gameRunning) return;
    const W = canvas.width, H = canvas.height;
    const cur = window.stkCurrent;

    // Advance the sliding piece
    if (cur) {
        cur.x += cur.speed * cur.dir;
        if (cur.x + cur.w > W) { cur.x = W - cur.w; cur.dir = -1; }
        else if (cur.x < 0)    { cur.x = 0;          cur.dir =  1; }
    }

    // ── Background ─────────────────────────────────────────────────
    ctx.fillStyle = '#0e0818';
    ctx.fillRect(0, 0, W, H);

    // Subtle horizontal scan-line grid
    ctx.strokeStyle = 'rgba(247,37,133,0.055)';
    ctx.lineWidth = 1;
    for (let y = 0; y < H; y += 22) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
    }

    // Side neon glow columns
    const lg = ctx.createLinearGradient(0, 0, W, 0);
    lg.addColorStop(0,   'rgba(247,37,133,0.07)');
    lg.addColorStop(0.12,'rgba(247,37,133,0)');
    lg.addColorStop(0.88,'rgba(247,37,133,0)');
    lg.addColorStop(1,   'rgba(247,37,133,0.07)');
    ctx.fillStyle = lg;
    ctx.fillRect(0, 0, W, H);

    // ── Plate ──────────────────────────────────────────────────────
    const plateVisY = STK_PLATE_Y + window.stkScrollY;
    if (plateVisY < H + STK_PLATE_H) {
        stkDrawPlate(plateVisY);
    }

    // ── Placed pieces ──────────────────────────────────────────────
    for (let i = 0; i < window.stkStack.length; i++) {
        const piece = window.stkStack[i];
        const py    = stkPieceY(i);
        if (py > H || py + STK_PIECE_H < 0) continue;
        if (piece.flash > 0) {
            piece.flash--;
            ctx.shadowColor = '#f5c842';
            ctx.shadowBlur  = 16;
        }
        stkDrawKimbap(piece.x, py, piece.w, STK_PIECE_H, piece.filling);
        ctx.shadowBlur = 0;
    }

    // ── Sliding piece ──────────────────────────────────────────────
    if (cur) {
        const py = stkPieceY(window.stkStack.length);

        // Dashed "perfect zone" guide aligned with the piece below
        const ref = window.stkStack.length > 0
            ? window.stkStack[window.stkStack.length - 1]
            : {x: canvas.width / 2 - window.GAME_STACKER.initW / 2, w: window.GAME_STACKER.initW};
        ctx.strokeStyle = 'rgba(245,200,66,0.28)';
        ctx.lineWidth   = 1;
        ctx.setLineDash([3, 4]);
        ctx.beginPath();
        ctx.roundRect(ref.x, py, ref.w, STK_PIECE_H, 3);
        ctx.stroke();
        ctx.setLineDash([]);

        ctx.shadowColor = '#f72585';
        ctx.shadowBlur  = 13;
        stkDrawKimbap(cur.x, py, cur.w, STK_PIECE_H, cur.filling);
        ctx.shadowBlur  = 0;
    }

    // ── Footer hint ────────────────────────────────────────────────
    ctx.font      = '5px "Press Start 2P",monospace';
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(247,37,133,0.55)';
    ctx.fillText('CLICK  /  SPACE  to stack!', W / 2, H - 6);
}

// ── Drawing helpers ───────────────────────────────────────────────────

function stkDrawPlate(y) {
    const CFG = window.GAME_STACKER;
    const pw  = CFG.initW + 50;
    const px  = canvas.width / 2 - pw / 2;

    // Drop shadow ellipse
    ctx.fillStyle = 'rgba(0,0,0,0.5)';
    ctx.beginPath();
    ctx.ellipse(canvas.width / 2, y + STK_PLATE_H + 5, pw / 2 + 2, 6, 0, 0, Math.PI * 2);
    ctx.fill();

    // Plate body
    ctx.fillStyle   = '#f4f0e8';
    ctx.strokeStyle = '#c8b89a';
    ctx.lineWidth   = 2;
    ctx.beginPath();
    ctx.roundRect(px, y, pw, STK_PLATE_H, [5, 5, 14, 14]);
    ctx.fill();
    ctx.stroke();

    // Rim highlight
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.beginPath();
    ctx.roundRect(px + 5, y + 4, pw - 10, 5, 3);
    ctx.fill();

    // Label
    ctx.font      = '5px "Press Start 2P",monospace';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#b0a085';
    ctx.fillText('~ PLATE ~', canvas.width / 2, y + STK_PLATE_H - 5);
}

function stkDrawKimbap(x, y, w, h, filling) {
    if (w < 4) return;

    // Drop shadow
    ctx.fillStyle = 'rgba(0,0,0,0.4)';
    ctx.beginPath();
    ctx.roundRect(x + 3, y + 3, w, h, 4);
    ctx.fill();

    // Nori outer (dark wrap)
    ctx.fillStyle = '#1a1f2e';
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, 4);
    ctx.fill();

    // Rice interior
    ctx.fillStyle = '#fffde7';
    ctx.beginPath();
    ctx.roundRect(x + 3, y + 2, w - 6, h - 4, 3);
    ctx.fill();

    // Filling circle in the center
    if (w > 14) {
        const fcx = x + w / 2;
        const fcy = y + h / 2;
        const fr  = Math.min(h / 4.5, (w - 10) / 5);
        ctx.fillStyle = filling;
        ctx.beginPath();
        ctx.arc(fcx, fcy, Math.max(2, fr), 0, Math.PI * 2);
        ctx.fill();
    }

    // Sesame seeds (only when wide enough)
    if (w > 32) {
        ctx.fillStyle = '#4a3728';
        const half = w / 2;
        const offsets = [[-0.28, 0], [0.28, 0]];
        offsets.forEach(([dx]) => {
            const sx = x + half + dx * half * 0.7;
            if (sx > x + 6 && sx < x + w - 6) {
                ctx.beginPath();
                ctx.arc(sx, y + h / 2, 1.4, 0, Math.PI * 2);
                ctx.fill();
            }
        });
    }
}

// ── Expose to window ──────────────────────────────────────────────────
window.startStacker  = startStacker;
window.endStacker    = endStacker;
window.stkDrop       = stkDrop;
window.stkDropTouch  = stkDropTouch;
window.stkKey        = stkKey;
